import { useEffect } from 'react';
import axios from 'axios';

import { getItem, storeItem } from '../helpers/store';
import { authApiBaseURL } from '../services/queries';
import { useSignOut } from './useSignOut';

// A 401 on these endpoints means "bad credentials", not "expired session", so they
// must not trigger a token refresh / sign-out. /auth/mfa/verify rejects invalid TOTP
// or backup codes with 401 while the user is still mid-login (no session yet); the
// forced-enrollment /auth/mfa/setup and /auth/mfa/enroll likewise 401 on an expired
// setup token, which should surface the expired view rather than a refresh/sign-out.
const WHITELISTED_URLS = [
  /^\/login$/,
  /^\/auth\/mfa\/verify$/,
  /^\/auth\/mfa\/setup$/,
  /^\/auth\/mfa\/enroll$/,
  /^\/shared_lists\/[^/]+\/contacts$/,
];

// Shared single-flight refresh state
let refreshPromise = null;

/**
 * Performs a single in-flight token refresh. Subsequent callers await the same promise.
 * Resolves when refresh succeeds (and token is stored) or throws on failure.
 */
const refreshAuthToken = async (signOut) => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const response = await fetch(`${authApiBaseURL}auth/refresh`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
    });

    if (response.ok) {
      const token = response.headers?.get('Authorization');
      if (token) {
        storeItem('authToken', token);
      }
      return token;
    }

    if (response.status === 401) {
      // Session is no longer valid
      signOut();
    }

    throw new Error('Failed to refresh auth token');
  })().finally(() => {
    // Allow next refresh attempts
    refreshPromise = null;
  });

  return refreshPromise;
};

/**
 * Check if a URL should be excluded from sign out behavior
 * @param {string} url - The URL to check
 * @returns {boolean} - True if the URL should be whitelisted
 */
const isUrlWhitelisted = (url = '') => {
  let pathname = url;

  if (url.includes('?')) {
    pathname = url.split('?')[0];
  }

  return WHITELISTED_URLS.some((pattern) => pattern.test(pathname));
};

export const shouldAttemptAuthRefresh = ({ status, originalRequest, authToken }) => {
  if (!originalRequest || originalRequest.retry || isUrlWhitelisted(originalRequest.url || '')) {
    return false;
  }

  return status === 401 || (status === 403 && Boolean(authToken));
};

export const useAxiosInterceptors = () => {
  const { signOut } = useSignOut();

  useEffect(() => {
    const reqId = axios.interceptors.request.use((config) => {
      const accessToken = getItem('authToken');

      if (accessToken) {
        // eslint-disable-next-line no-param-reassign
        config.headers.Authorization = accessToken;
      }

      return config;
    });

    const resId = axios.interceptors.response.use(
      (response) => {
        const { authorization } = response?.headers;

        if (authorization) {
          storeItem('authToken', authorization);
        }

        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        const status = error?.response?.status;
        const authToken = getItem('authToken');

        if (shouldAttemptAuthRefresh({ status, originalRequest, authToken })) {
          originalRequest.retry = true;

          try {
            await refreshAuthToken(signOut);
            // Retry the original request after refresh. It will pick up the new token via request interceptor.
            return axios(originalRequest);
          } catch (refreshError) {
            // Refresh failed; propagate original error
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};
